import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Both staff `ApiResponseError` and the portal's plain Error
        // (decorated via Object.assign in portal/auth.tsx) carry a
        // numeric `.status` property. Read it duck-typed so the auth
        // failure modes don't slip past the check and trigger a retry
        // storm — that was the root cause of the 6× /portfolio 401
        // cascade after a candidate session expired.
        const status = (error as { status?: number } | null)?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: false,
    },
  },
});

// Query key factories — centralise key shapes to avoid string drift
export const keys = {
  home: {
    counts: () => ['home', 'counts'] as const,
    activity: () => ['home', 'activity'] as const,
  },
  candidates: {
    list: (params: Record<string, unknown>) => ['candidates', 'list', params] as const,
    detail: (id: string) => ['candidates', id] as const,
    timeline: (id: string) => ['candidates', id, 'timeline'] as const,
    scores: (id: string) => ['candidates', id, 'scores'] as const,
    flags: (id: string) => ['candidates', id, 'flags'] as const,
  },
  reviews: {
    queue: () => ['reviews', 'queue'] as const,
    candidates: () => ['reviews', 'candidates'] as const,
    byCandidate: (candidateId: string, page: number) =>
      ['reviews', 'candidate', candidateId, page] as const,
    detail: (scoreId: string) => ['reviews', scoreId] as const,
  },
  passports: {
    queue: () => ['passports', 'queue'] as const,
    detail: (id: string) => ['passports', id] as const,
  },
  items: {
    list: (params: Record<string, unknown>) => ['items', 'list', params] as const,
    detail: (id: string) => ['items', id] as const,
  },
  framework: {
    current: () => ['framework', 'current'] as const,
  },
  users: {
    list: () => ['users', 'list'] as const,
  },
  audit: {
    list: (params: Record<string, unknown>) => ['audit', 'list', params] as const,
  },
  clients: {
    list: () => ['clients', 'list'] as const,
  },
  sourced: {
    batches: (batchId: string) => ['sourced', 'batches', batchId] as const,
  },
} as const;
